import { template as _$template } from "solid-js/web";
import { delegateEvents as _$delegateEvents } from "solid-js/web";
import { setAttribute as _$setAttribute } from "solid-js/web";
import { effect as _$effect } from "solid-js/web";
import { insert as _$insert } from "solid-js/web";
import { createComponent as _$createComponent } from "solid-js/web";
import { use as _$use } from "solid-js/web";
var _tmpl$ = /*#__PURE__*/_$template(`<p class=esc-menu-overlay__status>The baked assets could not be loaded.`),
  _tmpl$2 = /*#__PURE__*/_$template(`<div class=esc-menu-overlay role=dialog aria-label="Mission menu"data-testid=esc-menu-overlay><canvas class=esc-menu-overlay__canvas>`);
import { createEffect, createSignal, onCleanup, onMount, Show } from "solid-js";
import { createEscMenuSurface } from "../shell/esc-menu-surface.ts";
import { createScreenRuntime } from "../shell/screen-runtime.ts";
import { ESC_MENU_KEY, normalizeKey } from "./keys.ts";
/**
 * The original's in-flight `ESC` board — *"YOUR MISSION IS NOT COMPLETE. DO YOU
 * WISH TO:"* — over the aeroplane it is asking about (G38).
 *
 * ## The one thing this does that the other three in-flight sheets do not
 *
 * The map, PREFERENCES and the debriefing all **replace** the picture: an opaque
 * 320×200 shell screen over a paused flight. This one is composited, because the
 * capture shows the original's board floating over the cockpit with the wings
 * and the gauges still visible round it — and because a dialog asking whether to
 * abandon a sortie is a strange thing to answer with the sortie hidden.
 *
 * That is why `ScreenRuntimeOptions.transparent` exists: the logical frame is
 * cleared to nothing rather than to black, the surface's screen carries
 * `chrome: "none"` so nothing fills it, and `present` composites instead of
 * laying black down first. The flight canvas underneath is frozen, not hidden —
 * the sim is paused the whole time the board is up.
 *
 * ## It swallows the keyboard, on purpose
 *
 * The reasoning is `PreferencesOverlay.tsx`'s in full and is not restated here:
 * a capture-phase `window` listener stops flight keys reaching the aeroplane
 * while a piece of chrome is over the viewport, `keyup` is deliberately let
 * through so a key held when the board opened can still release, and the pointer
 * half is settled by this component's root class being in `keys.ts`'s
 * `UI_OVERLAY_CLASSES` — without which a click on RETURN TO BASE also fires the
 * guns. The joystick's third mechanism is the route's `blocked` disjunction.
 *
 * The one local difference: `Escape` **is** handled here, where the preferences
 * panel deliberately passes it to the runtime. This board is one screen deep,
 * `Escape` is the key that opened it, and a toggle key that cannot shut its own
 * board is the defect the map and PREFERENCES both avoid by handling their own.
 */
function EscMenuBoard(props) {
  const surface = createEscMenuSurface({
    situation: () => props.session.situation(),
    onContinue: () => props.session.onContinue(),
    onReturnToBase: () => props.session.onReturnToBase(),
    onRestart: () => props.session.onRestart(),
    onBailOut: () => props.session.onBailOut()
  });
  // No title theme, for `PreferencesOverlay.tsx`'s reason: this opens over a
  // sortie that is already making noise. Transparent, for this file's own.
  const runtime = createScreenRuntime(surface, () => {}, {
    music: false,
    transparent: true
  });
  const [status, setStatus] = createSignal(runtime.status());
  runtime.subscribe(setStatus);
  let canvas;
  function onKeyDown(event) {
    if (!props.session.open()) return;
    if (event.metaKey || event.ctrlKey || event.altKey) return;
    event.stopPropagation();
    event.preventDefault();
    if (normalizeKey(event.key) === ESC_MENU_KEY) {
      props.session.onContinue();
      return;
    }
    runtime.keyDown(event);
  }
  onMount(() => {
    runtime.mount(canvas);
    const observer = new ResizeObserver(() => runtime.refresh());
    if (canvas) observer.observe(canvas);
    window.addEventListener("keydown", onKeyDown, {
      capture: true
    });
    onCleanup(() => {
      observer.disconnect();
      window.removeEventListener("keydown", onKeyDown, {
        capture: true
      });
      runtime.dispose();
    });
  });

  // Repaint on every open, and again whenever the situation the board is
  // derived from moves: the SCORE counts up under the buttons while it is
  // open, and a guard can arrive (an enemy closes) while the player reads it.
  createEffect(() => {
    if (!props.session.open()) return;
    props.session.situation();
    runtime.refresh();
  });
  return (() => {
    var _el$ = _tmpl$2(),
      _el$2 = _el$.firstChild;
    _el$2.$$pointermove = event => runtime.pointerMove(event);
    _el$2.$$pointerdown = event => runtime.pointerDown(event);
    _$use(element => canvas = element, _el$2);
    _$insert(_el$, _$createComponent(Show, {
      get when() {
        return status() === "failed";
      },
      get children() {
        return _tmpl$();
      }
    }), null);
    _$effect(_p$ => {
      var _v$ = !props.session.open(),
        _v$2 = !props.session.open();
      _v$ !== _p$.e && _el$.classList.toggle("esc-menu-overlay--hidden", _p$.e = _v$);
      _v$2 !== _p$.t && _$setAttribute(_el$, "aria-hidden", _p$.t = _v$2);
      return _p$;
    }, {
      e: undefined,
      t: undefined
    });
    return _el$;
  })();
}

/**
 * Lazily mounted, on `MapOverlay.tsx`'s rule: a sortie nobody presses `ESC` on
 * pays nothing for the shell fonts, the palette or a `ScreenRuntime`.
 *
 * The latch is read off the **session**, not held here. Held here it was reset
 * every time the board shut, because `flyChrome` re-creates its whole subtree
 * whenever a signal it reads changes — so the "lazy" mount ran on every single
 * press. See `esc-menu-session.ts`.
 */
export function FlightEscMenu(props) {
  return _$createComponent(Show, {
    get when() {
      return props.session.everOpened();
    },
    get children() {
      return _$createComponent(EscMenuBoard, {
        get session() {
          return props.session;
        }
      });
    }
  });
}
_$delegateEvents(["pointerdown", "pointermove"]);